import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { generateLabelList } from 'helpers/generators';
import useIsMobile from 'hooks/useIsMobile';
import { useTextOverflow } from 'hooks/useTextOverflow';
import {
  MouseEvent,
  ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';
import { hasAtLeastItems, isEmptyArray } from 'shared/utils/array';
import { isNonEmptyLabelledEntity } from 'shared/utils/value';
import { ListCellWithModalProps } from './types';
import {
  listCellChipSx,
  listCellCloseButtonSx,
  listCellContainerSx,
  listCellDialogPaperSx,
  listCellDialogTitleSx,
  listCellMobileTextSx,
  listCellTextSx,
} from './styles';

const ACTION_CHIP_SPACE_PX = 72;

export function ListCellWithModal({
  items,
  label,
  emptyPlaceholder = '-',
}: ListCellWithModalProps): ReactElement {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const labelledItems = items.filter(isNonEmptyLabelledEntity);
  const labelledItemsText = generateLabelList(labelledItems);
  const hasAtLeastTwoItems = hasAtLeastItems(labelledItems, 2);

  const tooltipTitle = (
    <Box>
      {labelledItems.map((item) => (
        <Typography key={item._id} variant='body2'>
          {item.label}
        </Typography>
      ))}
    </Box>
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setOpen(true);
  }, []);

  const showEllipsis = useTextOverflow(
    textRef,
    containerRef,
    labelledItemsText,
    hasAtLeastTwoItems ? ACTION_CHIP_SPACE_PX : 0
  );

  if (isEmptyArray(labelledItems)) {
    return <Typography variant='body2'>{emptyPlaceholder}</Typography>;
  }

  if (isMobile) {
    return (
      <Typography variant='body2' sx={listCellMobileTextSx}>
        {labelledItemsText}
      </Typography>
    );
  }

  return (
    <>
      <Box ref={containerRef} sx={listCellContainerSx}>
        <Typography
          ref={textRef}
          variant='body2'
          sx={listCellTextSx(showEllipsis, hasAtLeastTwoItems)}
        >
          {labelledItemsText}
        </Typography>
        {hasAtLeastTwoItems && showEllipsis && (
          <Tooltip title={tooltipTitle}>
            <Chip
              size='small'
              clickable
              onClick={handleOpen}
              label={`+ ${labelledItems.length}`}
              sx={listCellChipSx}
            />
          </Tooltip>
        )}
      </Box>
      {open ? (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth='sm'
          fullWidth
          PaperProps={{
            sx: listCellDialogPaperSx,
          }}
        >
          <DialogTitle sx={listCellDialogTitleSx}>
            {label}
            <IconButton
              size='small'
              onClick={handleClose}
              sx={listCellCloseButtonSx}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <List dense>
              {labelledItems.map((item) => (
                <ListItem key={item._id} sx={{ px: 0 }}>
                  <ListItemText primary={item.label} />
                </ListItem>
              ))}
            </List>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
